import { RecentReceptionsApiService } from '@api_patient/recent_receptions_api.service';
import { AuthService } from '@application/authentication/auth.service';
import { withTranslation } from '@application/i18n/with_translation';
import { UnitNode, UnitService } from '@application/unit.service';
import { UniversalConfig } from '@application/universal_config.service';
import { ActionCard, Flex, Icon, Text } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { Shimmer } from '@component/new_shimmer.component';
import {
	AsyncSignal,
	AsyncSignalValue,
	asyncSignalAccessible,
	asyncSignalError,
	asyncSignalIdle,
	asyncSignalPending,
} from '@cortex-shim/async_signal.component';
import { RouterService } from '@cortex-shim/router.service';
import { signal } from '@preact/signals-react';
import { Injectable, Scopes, useResolve, useResolveController, withNewContainer } from '@visiba/instantiation';
import { EmptyState } from './_empty_message.component';
import { useViewFindReceptionTempNavigationContext } from './_temp_navigation.context';

export const RecentReceptions = withNewContainer(function RecentReceptions() {
	const controller = useResolveController(Controller);
	const routerService = useResolve(RouterService);
	const { __TEMP__onNavigate } = useViewFindReceptionTempNavigationContext();

	function handleOnReceptionClick(reception: UnitNode) {
		if (__TEMP__onNavigate) {
			__TEMP__onNavigate(`/${reception.routeWithoutLicenceholder}`);
			return;
		}

		routerService.navigateByUrl(`/${reception.routeWithoutLicenceholder}`);
	}

	return (
		<Flex gap={tokens.spacing.x1}>
			<Text variant='labelSemiBold'>{withTranslation('View.FindReception.RecentReceptions.Title')}</Text>
			<AsyncSignal signal={controller.recentReceptions} pending={<Skeleton />}>
				{(recentReceptions) => {
					return (
						<>
							{recentReceptions.length === 0 ? (
								<EmptyState>{withTranslation('View.FindReception.RecentReceptions.EmptyMessage')}</EmptyState>
							) : (
								<Flex gap={tokens.spacing.x1}>
									{recentReceptions.map((reception) => (
										<ActionCard.Root onClick={() => handleOnReceptionClick(reception)} key={reception.id} icon='house'>
											<ActionCard.Title>{reception.name}</ActionCard.Title>
										</ActionCard.Root>
									))}
								</Flex>
							)}
						</>
					);
				}}
			</AsyncSignal>
		</Flex>
	);
});

function Skeleton() {
	return <Shimmer width='100%' height={tokens.spacing.x8} />;
}

@Injectable({
	scope: Scopes.Scoped,
})
class Controller {
	public recentReceptions = signal<AsyncSignalValue<UnitNode[]>>(asyncSignalIdle());

	constructor(
		private readonly recentReceptionsApiService: RecentReceptionsApiService,
		private readonly unitService: UnitService,
		private readonly authService: AuthService,
		private readonly universalConfig: UniversalConfig,
	) {
		// Empty
	}

	whenMount() {
		if (!this.authService.isSignedIn()) {
			const recentReceptions: UnitNode[] = [];

			const selectedUnitId = this.universalConfig.selectedUnitId;
			if (selectedUnitId) {
				const recentReception = this.unitService.getUnitNodeInTree(selectedUnitId);

				if (recentReception != null && recentReception.isEntrance && recentReception.receptionId != -1) {
					recentReceptions.push({
						...recentReception,
					});
				}
			}

			this.recentReceptions.value = asyncSignalAccessible(recentReceptions);
			return;
		}

		this.fetchRecentReceptionsFromApi();
	}

	private async fetchRecentReceptionsFromApi() {
		this.recentReceptions.value = asyncSignalPending();

		const result = await this.recentReceptionsApiService.getRecentlyVisited(2);
		if (result.err) {
			this.recentReceptions.value = asyncSignalError();
			return;
		}

		const recentReceptions: UnitNode[] = [];

		result.ok.data.forEach((reception) => {
			const unit = this.unitService.getUnitNodeInTreeByReceptionId(reception.ReceptionId);
			if (unit != null) {
				recentReceptions.push(unit);
			}
		});

		this.recentReceptions.value = asyncSignalAccessible(recentReceptions);
	}
}
